import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import { preventUnhandled } from '@atlaskit/pragmatic-drag-and-drop/prevent-unhandled';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { DragType } from '@databases/drag';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import {
  selectCurrentSelectionMode,
  selectSelectedEntryIds,
  selectSelectedFieldIds,
} from '@databases/redux/cellSelections/selectors';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, { type FunctionComponent, useEffect, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

const activeColor = token('color.background.accent.blue.subtler.pressed', colors.B200);
const deletionActiveColor = token(
  'color.background.accent.red.subtler.pressed',
  colors.R400
);

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
export const StyledCaret = styled.div<{ isActive: boolean; isDeletionActive: boolean }>`
  z-index: 1;
  display: flex;
  transition: opacity 0.2s;
  // Preventing items getting :hover effects during a drag
  ${({ isActive }) => isActive && `pointerEvents: 'none';`}

  position: absolute;
  justify-content: center;
  align-items: center;

  bottom: ${token('space.negative.075', '-6px')};
  right: -6.5px;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 10px solid ${token('color.text.inverse', colors.N0)};
  cursor: ns-resize;

  &:after {
    position: absolute;
    content: '';
  }

  &:hover {
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 16px solid ${token('color.text.inverse', colors.N0)};
    bottom: -10px;
    right: -10.5px;

    &:after {
      top: -14px;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      ${({ isDeletionActive }) =>
        `border-top: 10px solid ${isDeletionActive ? deletionActiveColor : activeColor}`};
    }
  }

  &:not(:hover) {
    &:after {
      top: ${token('space.negative.100', '-8px')};
      border-left: 3px solid transparent;
      border-right: 3px solid transparent;
      ${({ isDeletionActive }) =>
        `border-top: 5px solid ${isDeletionActive ? deletionActiveColor : activeColor}`};
    }
  }
`;

export interface DragCaretProps {
  contentId: string;
  canEditContent: boolean;
  fieldId: string;
  entryId: string;
  isSelected: boolean;
  isDeletionActive: boolean;
  onIsSelectedChange?: (options?: { isMultiSelectOn?: boolean }) => void;
}

/**
 * Renders a drag caret that indicates that the user can drag to fill cells.
 */
const DragCaret: FunctionComponent<DragCaretProps> = ({
  contentId,
  canEditContent,
  fieldId,
  entryId,
  isSelected,
  isDeletionActive,
  onIsSelectedChange,
}) => {
  const { formatMessage } = useIntl();
  const ref = React.useRef<HTMLDivElement>(null);
  const store = defaultStoreUtils.useStore();

  const [isDragging, setIsDragging] = useState(false);
  useEffect(() => {
    if (!ref.current) {
      return;
    }
    return draggable({
      element: ref.current,
      getInitialData: () => {
        if (!isSelected) {
          return {
            type: DragType.FILL_VALUES,
            selectedFieldIds: [fieldId],
            selectedEntryIds: [entryId],
          };
        }

        const currentSelectionMode = selectCurrentSelectionMode(store.getState(), {
          contentId,
        });
        const database = selectDatabaseByContentIdRequired(store.getState(), {
          contentId,
        });
        const selectedFieldIds =
          currentSelectionMode === 'row'
            ? database.fields
            : selectSelectedFieldIds(store.getState(), {
                contentId,
              });
        const selectedEntryIds =
          currentSelectionMode === 'column'
            ? database.entries
            : selectSelectedEntryIds(store.getState(), {
                contentId,
              });
        return {
          type: DragType.FILL_VALUES,
          selectedFieldIds: [...selectedFieldIds, fieldId],
          selectedEntryIds: [...selectedEntryIds, entryId],
        };
      },
      onDragStart: ({ source }) => {
        if (!isSelected && source.data.type === DragType.FILL_VALUES) {
          onIsSelectedChange?.();
        }
        setIsDragging(true);
      },
      onDrop: () => {
        setIsDragging(false);
        preventUnhandled.stop();
      },
      onGenerateDragPreview: ({ nativeSetDragImage }) => {
        // disable the native drag preview
        disableNativeDragPreview({ nativeSetDragImage });
        // we don't want any native drop animation for when the user
        // does not drop on a drop target. we want the drag to finish immediately
        preventUnhandled.start();
      },
    });
  }, [contentId, entryId, fieldId, isSelected, store, onIsSelectedChange]);

  if (!canEditContent) {
    return null;
  }

  return (
    <Tooltip
      content={() => formatMessage(messages['table-layout.drag-to-fill-tooltip'])}
      position="right"
    >
      <StyledCaret
        isActive={isDragging}
        isDeletionActive={isDeletionActive}
        ref={ref}
        data-testid="database-cell-drag-caret"
      />
    </Tooltip>
  );
};

export default DragCaret;
